// ============================================================================
// UIF business dates — America/Chicago, always.
// ============================================================================
// DAVID'S RULING (2026-07-20), program-wide and not PR-local: every calendar-day
// determination the platform makes is in America/Chicago, independent of the
// browser's timezone and of the Lambda runtime's. That governs execution dates,
// milestone deadlines, payment dates, FSA windows and incentive-cycle
// boundaries — anywhere "what day is it" decides an outcome.
//
// WHY A HELPER RATHER THAN AN EXPRESSION. This value has now been wrong three
// times in three different ways, each fix introducing the next:
//   1. toISOString().slice(0,10)      -> the UTC day, not the user's
//   2. toLocaleDateString('en-CA')    -> right format by convention only; not a
//                                        spec-guaranteed ISO formatter
//   3. getFullYear/getMonth/getDate   -> the BROWSER's day, which then
//                                        disagreed with the server's UTC day and
//                                        produced 422s for anyone ahead of UTC
// One definition, one place, both sides. `api/src/lib/businessDate.ts` is the
// server twin and MUST stay semantically identical — same rule as the
// db/ + api/migrations/ mirror discipline.
//
// NEVER use toLocaleDateString() output for this. Its format is locale-data
// dependent; that assumption is what started the thread.
// ============================================================================

export const UIF_BUSINESS_TZ = 'America/Chicago';

/**
 * The current UIF business date as `YYYY-MM-DD`.
 *
 * Parts are requested explicitly and reassembled, so the output format is ours
 * rather than the formatter's — no locale can reorder or re-punctuate it. The
 * DST offset is resolved by the runtime's tz database, never hard-coded, so
 * CDT (UTC-5) and CST (UTC-6) are both handled without a branch here.
 */
export function businessToday(now: Date = new Date()): string {
  return businessDateOf(now);
}

/** The UIF business date of an arbitrary instant, as `YYYY-MM-DD`. */
export function businessDateOf(instant: Date): string {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: UIF_BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  // DEFENCE IN DEPTH, not a documented failure mode. ECMA-402 requires the
  // constructor to throw a RangeError for an unsupported IANA name, and official
  // Node builds ship full ICU — so a COMPLIANT runtime cannot silently resolve
  // this to UTC. The assertion exists because the cost of being wrong here is a
  // whole-program date shift that no test in the suite would catch (the dev
  // host's own zone is America/Chicago, so a UTC fallback looks correct locally
  // for most of the day). It guards a small-ICU or non-conforming build, and it
  // fails loudly instead of quietly.
  const resolved = fmt.resolvedOptions().timeZone;
  if (resolved !== UIF_BUSINESS_TZ) {
    throw new Error(
      `businessDate: runtime resolved timeZone "${resolved}" instead of "${UIF_BUSINESS_TZ}" — refusing to derive a business date from the wrong zone`,
    );
  }

  const parts = fmt.formatToParts(instant);
  const pick = (type: string): string => {
    const p = parts.find((x) => x.type === type);
    if (!p) throw new Error(`businessDate: formatter omitted the "${type}" part`);
    return p.value;
  };

  return `${pick('year')}-${pick('month')}-${pick('day')}`;
}
